import { IAPProduct, IAPPurchaseResult } from "../types/iap";

declare global {
  interface Window {
    webkit?: {
      messageHandlers: {
        fetchIAPProducts?: {
          postMessage: (data: any) => void;
        };
        purchaseIAPProduct?: {
          postMessage: (data: any) => void;
        };
        restoreIAPPurchases?: {
          postMessage: (data: any) => void;
        };
        confirmIAPTransaction?: {
          postMessage: (data: any) => void;
        };
        getIAPReceipt?: {
          postMessage: (data: any) => void;
        };
      };
    };
    AndroidBilling?: {
      fetchProducts: (productIds: string) => void;
      purchaseProduct: (productId: string) => void;
      restorePurchases: () => void;
      acknowledgePurchase: (purchaseToken: string) => void;
    };
  }
}

export interface AndroidExistingPurchase {
  productId: string;
  purchaseToken: string;
  orderId: string;
  purchaseTime: number;
  signature: string;
  originalJson: string;
}

export class IAPManager {
  private static instance: IAPManager;
  private products: IAPProduct[] = [];
  private isIOSApp: boolean = false;
  private isAndroidApp: boolean = false;
  private platform: "ios" | "android" | "web" = "web";

  private constructor() {
    // Check if running in iOS WKWebView
    this.isIOSApp = !!window.webkit?.messageHandlers?.fetchIAPProducts;

    // Check if running in Android app (AndroidBilling will be injected after WebView loads)
    this.isAndroidApp = !!window.AndroidBilling;

    if (!this.isAndroidApp) {
      // Wait for Android billing to be ready
      window.addEventListener("androidBillingReady", () => {
        this.isAndroidApp = !!window.AndroidBilling;
        if (this.isAndroidApp) {
          this.platform = "android";
          this.setupEventListeners();
          console.info("IAP Manager initialized - Android app detected");
        }
      });
    }

    if (this.isIOSApp) {
      this.platform = "ios";
      this.setupEventListeners();
      console.info("IAP Manager initialized - iOS app detected");
    } else if (this.isAndroidApp) {
      this.platform = "android";
      this.setupEventListeners();
      console.info("IAP Manager initialized - Android app detected");
    } else {
      console.info("IAP Manager initialized - web browser (no IAP)");
    }
  }

  static getInstance(): IAPManager {
    if (!IAPManager.instance) {
      IAPManager.instance = new IAPManager();
    }
    return IAPManager.instance;
  }

  isAvailable(): boolean {
    return this.isIOSApp || this.isAndroidApp;
  }

  getPlatform(): "ios" | "android" | "web" {
    return this.platform;
  }

  private setupEventListeners() {
    // iOS event listeners
    if (this.isIOSApp) {
      // Products loaded successfully
      window.addEventListener("iapProductsLoaded", (event: any) => {
        this.products = event.detail;
        window.dispatchEvent(
          new CustomEvent("iapProductsReady", { detail: this.products })
        );
      });

      // Products load failed
      window.addEventListener("iapProductsLoadFailed", (event: any) => {
        window.dispatchEvent(
          new CustomEvent("iapProductsReady", { detail: [] })
        );
      });
    }

    // Android event listeners
    if (this.isAndroidApp) {
      // Products loaded successfully
      window.addEventListener("androidProductsLoaded", (event: any) => {
        this.products = event.detail;
        window.dispatchEvent(
          new CustomEvent("iapProductsReady", { detail: this.products })
        );
      });

      // Products load failed
      window.addEventListener("androidProductsLoadFailed", (event: any) => {
        console.error("Android products load failed:", event.detail);
        window.dispatchEvent(
          new CustomEvent("iapProductsReady", { detail: [] })
        );
      });
    }

    // iOS Purchase events
    if (this.isIOSApp) {
      // Purchase success
      window.addEventListener("iapPurchaseSuccess", (event: any) => {
        window.dispatchEvent(
          new CustomEvent("iapPurchaseComplete", {
            detail: {
              success: true,
              productId: event.detail.productId,
              receipt: event.detail.receipt,
            },
          })
        );
      });

      // Purchase failed
      window.addEventListener("iapPurchaseFailed", (event: any) => {
        console.error("IAP purchase failed:", event.detail);
        window.dispatchEvent(
          new CustomEvent("iapPurchaseComplete", {
            detail: {
              success: false,
              productId: event.detail.productId,
              error: event.detail.error,
            },
          })
        );
      });
    }

    // Android Purchase events
    if (this.isAndroidApp) {
      // Purchase success
      window.addEventListener("androidPurchaseSuccess", (event: any) => {
        window.dispatchEvent(
          new CustomEvent("iapPurchaseComplete", {
            detail: {
              success: true,
              productId: event.detail.productId,
              receipt: {
                purchaseToken: event.detail.purchaseToken,
                orderId: event.detail.orderId,
                purchaseTime: event.detail.purchaseTime,
                signature: event.detail.signature,
                originalJson: event.detail.originalJson,
              },
            },
          })
        );
      });

      // Purchase failed
      window.addEventListener("androidPurchaseFailed", (event: any) => {
        console.error("Android purchase failed:", event.detail);
        window.dispatchEvent(
          new CustomEvent("iapPurchaseComplete", {
            detail: {
              success: false,
              error: event.detail.error,
            },
          })
        );
      });
    }

  }

  async fetchProducts(productIds: string[]): Promise<IAPProduct[]> {
    if (!this.isAvailable()) {
      console.warn("IAP not available - not running in mobile app");
      return [];
    }

    return new Promise((resolve) => {
      const handler = (event: any) => {
        window.removeEventListener("iapProductsReady", handler);
        resolve(event.detail);
      };

      window.addEventListener("iapProductsReady", handler);

      // Add timeout in case products never load
      setTimeout(() => {
        window.removeEventListener("iapProductsReady", handler);
        console.error("IAP product fetch timeout");
        resolve([]);
      }, 10000); // 10 second timeout

      if (this.isIOSApp) {
        window.webkit!.messageHandlers.fetchIAPProducts!.postMessage(
          productIds
        );
      } else if (this.isAndroidApp && window.AndroidBilling) {
        window.AndroidBilling.fetchProducts(JSON.stringify(productIds));
      }
    });
  }

  async purchaseProduct(productId: string): Promise<IAPPurchaseResult> {
    if (!this.isAvailable()) {
      console.warn("IAP not available - not running in mobile app");
      return { success: false, error: "Not in mobile app" };
    }

    return new Promise((resolve) => {
      const handler = (event: any) => {
        window.removeEventListener("iapPurchaseComplete", handler);
        resolve(event.detail);
      };

      window.addEventListener("iapPurchaseComplete", handler);

      // Add timeout in case purchase gets stuck
      setTimeout(() => {
        window.removeEventListener("iapPurchaseComplete", handler);
        console.error("IAP purchase timeout");
        resolve({ success: false, error: "Purchase timeout" });
      }, 60000); // 60 second timeout (purchases can take a while)

      if (this.isIOSApp) {
        window.webkit!.messageHandlers.purchaseIAPProduct!.postMessage(
          productId
        );
      } else if (this.isAndroidApp && window.AndroidBilling) {
        window.AndroidBilling.purchaseProduct(productId);
      }
    });
  }

  // Tells native it's now safe to finish/acknowledge a purchase - only call this
  // once the server has confirmed it recorded the transaction.
  confirmTransaction(productId: string): void {
    if (this.isIOSApp && window.webkit?.messageHandlers?.confirmIAPTransaction) {
      window.webkit.messageHandlers.confirmIAPTransaction.postMessage(productId);
    }
  }

  acknowledgePurchase(purchaseToken: string): void {
    // Guard against an old app binary whose native bridge predates this method -
    // the web client deploys independently and ahead of app store review, so this
    // must degrade gracefully instead of throwing until the app itself updates.
    if (this.isAndroidApp && typeof window.AndroidBilling?.acknowledgePurchase === "function") {
      window.AndroidBilling.acknowledgePurchase(purchaseToken);
    }
  }

  // Reads the on-device receipt with no StoreKit side effects (no prompts) -
  // used for a silent background sync. iOS-only; Android has no equivalent
  // (queryExistingPurchases is already prompt-free).
  private getReceipt(): Promise<string> {
    if (!this.isIOSApp || !window.webkit?.messageHandlers?.getIAPReceipt) {
      return Promise.resolve("");
    }

    return new Promise((resolve) => {
      const handler = (event: any) => {
        window.removeEventListener("iapReceiptFetched", handler);
        resolve(event.detail?.receipt || "");
      };

      window.addEventListener("iapReceiptFetched", handler);

      setTimeout(() => {
        window.removeEventListener("iapReceiptFetched", handler);
        resolve("");
      }, 5000);

      window.webkit!.messageHandlers.getIAPReceipt!.postMessage(null);
    });
  }

  // Heavier iOS restore flow (SKPaymentQueue.restoreCompletedTransactions) - can
  // prompt reauth if the local receipt is stale/missing, so it's reserved for the
  // explicit "Restore Purchases" button rather than a silent background sync.
  private restoreAndGetReceipt(): Promise<string> {
    if (!this.isIOSApp || !window.webkit?.messageHandlers?.restoreIAPPurchases) {
      return Promise.resolve("");
    }

    return new Promise((resolve) => {
      const cleanup = () => {
        window.removeEventListener("iapRestoreCompleted", completedHandler);
        window.removeEventListener("iapRestoreFailed", failedHandler);
      };
      const completedHandler = (event: any) => {
        cleanup();
        resolve(event.detail?.receipt || "");
      };
      const failedHandler = () => {
        cleanup();
        resolve("");
      };

      window.addEventListener("iapRestoreCompleted", completedHandler);
      window.addEventListener("iapRestoreFailed", failedHandler);

      setTimeout(() => {
        cleanup();
        resolve("");
      }, 30000);

      window.webkit!.messageHandlers.restoreIAPPurchases!.postMessage(null);
    });
  }

  // Lightweight, prompt-free lookup of the account's existing Google Play purchases -
  // used both for a silent background sync and the manual "Restore Purchases" button.
  private queryExistingPurchases(): Promise<AndroidExistingPurchase[]> {
    const androidBilling = window.AndroidBilling;
    if (!this.isAndroidApp || !androidBilling) {
      return Promise.resolve([]);
    }

    return new Promise((resolve) => {
      const handler = (event: any) => {
        window.removeEventListener("androidExistingPurchases", handler);
        resolve(event.detail || []);
      };

      window.addEventListener("androidExistingPurchases", handler);

      setTimeout(() => {
        window.removeEventListener("androidExistingPurchases", handler);
        resolve([]);
      }, 10000);

      androidBilling.restorePurchases();
    });
  }

  private syncWithServer(
    payload:
      | { platform: "apple"; receiptData: string }
      | { platform: "google"; purchases: Array<{ productId: string; purchaseToken: string; signature?: string; originalJson?: string }> },
  ): Promise<{ granted: number; revoked: number }> {
    const socket = (window as any).socket;
    if (!socket) {
      return Promise.resolve({ granted: 0, revoked: 0 });
    }

    return new Promise((resolve) => {
      const handler = (response: { success: boolean; granted?: number; revoked?: number }) => {
        socket.off("iap-restore-complete", handler);
        resolve({ granted: response.granted || 0, revoked: response.revoked || 0 });
      };

      socket.on("iap-restore-complete", handler);

      setTimeout(() => {
        socket.off("iap-restore-complete", handler);
        resolve({ granted: 0, revoked: 0 });
      }, 15000);

      socket.emit("iap-restore", payload);
    });
  }

  // Reconciles our records against Apple/Google - backfills anything we missed and
  // (iOS only) revokes anything Apple reports as refunded. Pass heavy=true for the
  // explicit "Restore Purchases" button (iOS: prompts reauth if needed); omit it for
  // a silent background sync when the shop opens.
  async syncPurchases(options: { heavy?: boolean } = {}): Promise<{ granted: number; revoked: number }> {
    if (!this.isAvailable()) {
      return { granted: 0, revoked: 0 };
    }

    if (this.isIOSApp) {
      const receipt = options.heavy ? await this.restoreAndGetReceipt() : await this.getReceipt();
      if (!receipt) return { granted: 0, revoked: 0 };
      return this.syncWithServer({ platform: "apple", receiptData: receipt });
    }

    if (this.isAndroidApp) {
      const purchases = await this.queryExistingPurchases();
      if (purchases.length === 0) return { granted: 0, revoked: 0 };
      return this.syncWithServer({
        platform: "google",
        purchases: purchases.map((p) => ({
          productId: p.productId,
          purchaseToken: p.purchaseToken,
          signature: p.signature,
          originalJson: p.originalJson,
        })),
      });
    }

    return { granted: 0, revoked: 0 };
  }

  getProducts(): IAPProduct[] {
    return this.products;
  }
}
